import { useState, useEffect } from "react";
import {
  RefreshCw,
  Link2,
  Unlink,
  CheckCircle,
  XCircle,
  Search,
  Clock,
  Eye,
  AlertCircle,
  Info
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { Button } from "@/components/ui/button";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { InputVenture } from "@/components/ui/input-venture";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StageMappingModal } from "@/components/admin/StageMappingModal";
import { useAllStageMappings } from "@/hooks/useStageMapping";

type Platform = 'ghl' | 'google_ads' | 'meta_ads';

interface Integration {
  id: string;
  provider: string;
  status: string | null;
  last_sync_at: string | null;
  sync_count: number | null;
  metadata: unknown;
}

interface ClientWithIntegrations {
  id: string;
  company_name: string;
  contact_name: string | null;
  email: string;
  package: string;
  integrations: Integration[];
  integrations_not_needed: string[];
}

const platformConfig: Record<Platform, { name: string; shortName: string; color: string }> = {
  ghl: {
    name: 'GoHighLevel',
    shortName: 'GHL',
    color: 'bg-orange-500',
  },
  google_ads: {
    name: 'Google Ads',
    shortName: 'Google',
    color: 'bg-green-500',
  },
  meta_ads: {
    name: 'Meta Ads',
    shortName: 'Meta',
    color: 'bg-blue-500',
  }
};

export function ClientIntegrationsTab({ dataVersion = 0 }: { dataVersion?: number }) {
  const [clients, setClients] = useState<ClientWithIntegrations[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [syncingClient, setSyncingClient] = useState<{ clientId: string; platform: Platform } | null>(null);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [connectingClient, setConnectingClient] = useState<ClientWithIntegrations | null>(null);
  const [connectingPlatform, setConnectingPlatform] = useState<Platform | null>(null);
  const [formData, setFormData] = useState({ apiToken: '', locationId: '', metaToken: '', metaAccountId: '' });
  const [connectLoading, setConnectLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedClientDetails, setSelectedClientDetails] = useState<ClientWithIntegrations | null>(null);
  const [disconnectDialog, setDisconnectDialog] = useState<{ open: boolean; clientId: string | null; platform: Platform | null }>({ open: false, clientId: null, platform: null });
  const [stageMappingClient, setStageMappingClient] = useState<{ id: string; name: string } | null>(null);
  const [agencyConnected, setAgencyConnected] = useState<boolean | null>(null); // null = loading
  const [agencyAccounts, setAgencyAccounts] = useState<{ id: string; name: string; isManager: boolean }[]>([]);
  const [linkingClient, setLinkingClient] = useState<string | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<Record<string, string>>({});
  const { byClient: stageMappingsByClient } = useAllStageMappings();

  useEffect(() => {
    fetchClientsWithIntegrations();
  }, [dataVersion]);

  useEffect(() => {
    fetchAgencyStatus();
  }, []);

  const fetchAgencyStatus = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('google-ads-accounts');
      if (error) throw error;
      setAgencyConnected(data.connected);
      if (data.connected) {
        setAgencyAccounts(data.accounts || []);
      }
    } catch {
      setAgencyConnected(false);
    }
  };

  const fetchClientsWithIntegrations = async () => {
    setLoading(true);
    try {
      // Fetch clients
      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select('id, company_name, contact_name, email, package, integrations_not_needed')
        .order('company_name');

      if (clientsError) throw clientsError;

      // Fetch only active integrations (revoked/disconnected ones should not show)
      const { data: integrationsData, error: integrationsError } = await supabase
        .from('integrations')
        .select('*')
        .eq('status', 'active');

      if (integrationsError) throw integrationsError;

      // Map integrations to clients
      const clientsWithIntegrations: ClientWithIntegrations[] = (clientsData || []).map(client => ({
        ...client,
        integrations: (integrationsData || []).filter(i => i.client_id === client.id)
      }));

      setClients(clientsWithIntegrations);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Error', {
        description: 'Failed to load clients',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAgencyConnect = async () => {
    setConnectLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('google-ads-oauth-start', {
        body: { agency: true }
      });
      if (error) throw error;

      const authWindow = window.open(data.authUrl, 'google_ads_agency_oauth', 'width=600,height=700');

      const handleMessage = (event: MessageEvent) => {
        if (event.data.type === 'oauth_callback' && event.data.platform === 'google_ads_agency') {
          window.removeEventListener('message', handleMessage);
          authWindow?.close();
          if (event.data.success) {
            toast.success('Agency Connected!', {
              description: 'Google Ads agency account connected successfully',
            });
            fetchAgencyStatus();
          } else {
            toast.error('Connection Failed', {
              description: event.data.error || 'OAuth authorization failed',
            });
          }
        }
      };
      window.addEventListener('message', handleMessage);
    } catch (error: unknown) {
      toast.error('Connection Failed', {
        description: error instanceof Error ? error.message : 'Failed to start OAuth',
      });
    } finally {
      setConnectLoading(false);
    }
  };

  const handleLinkAccount = async (clientId: string) => {
    const customerId = selectedAccountId[clientId];
    if (!customerId) {
      toast.error('Select an account first');
      return;
    }
    setLinkingClient(clientId);
    try {
      const mccAccount = agencyAccounts.find(a => a.isManager);
      const selectedAccount = agencyAccounts.find(a => a.id === customerId);
      const { error } = await supabase.functions.invoke('google-ads-connect', {
        body: {
          action: 'link',
          clientId,
          customerId,
          loginCustomerId: mccAccount?.id || null,
          accountName: selectedAccount?.name || null,
        }
      });
      if (error) throw error;
      toast.success('Account Linked', {
        description: `${selectedAccount?.name || customerId} linked successfully`,
      });
      fetchClientsWithIntegrations();
    } catch (error: unknown) {
      toast.error('Link Failed', {
        description: error instanceof Error ? error.message : 'Failed to link account',
      });
    } finally {
      setLinkingClient(null);
    }
  };

  const handleUnlinkAccount = async (clientId: string) => {
    try {
      const { error } = await supabase.functions.invoke('google-ads-connect', {
        body: { action: 'unlink', clientId }
      });
      if (error) throw error;
      toast.success('Account Unlinked', {
        description: 'Google Ads account unlinked and data cleared',
      });
      fetchClientsWithIntegrations();
    } catch (error: unknown) {
      toast.error('Unlink Failed', {
        description: error instanceof Error ? error.message : 'Failed to unlink account',
      });
    }
  };

  const handleAgencyDisconnect = async () => {
    try {
      const { error } = await supabase.functions.invoke('google-ads-connect', {
        body: { action: 'disconnect-agency' }
      });
      if (error) throw error;
      toast.success('Agency Disconnected', {
        description: 'Google Ads agency disconnected and all client data cleared',
      });
      setAgencyConnected(false);
      setAgencyAccounts([]);
      fetchClientsWithIntegrations();
    } catch (error: unknown) {
      toast.error('Disconnect Failed', {
        description: error instanceof Error ? error.message : 'Failed to disconnect agency',
      });
    }
  };

  const getIntegration = (client: ClientWithIntegrations, platform: Platform) => {
    return client.integrations.find(i => i.provider === platform);
  };

  const handleOpenConnectModal = (client: ClientWithIntegrations, platform: Platform) => {
    setConnectingClient(client);
    setConnectingPlatform(platform);
    setFormData({ apiToken: '', locationId: '', metaToken: '', metaAccountId: '' });
    setConnectModalOpen(true);
  };

  const handleConnect = async () => {
    if (!connectingClient || !connectingPlatform) return;
    setConnectLoading(true);

    try {
      if (connectingPlatform === 'ghl') {
        // GHL uses OAuth Marketplace flow — redirect in same tab (popups get blocked)
        const { data, error } = await supabase.functions.invoke('ghl-oauth-start', {
          body: { clientId: connectingClient.id }
        });

        if (error) throw error;

        // Navigate in same tab — callback will redirect back to command center
        window.location.href = data.authUrl;
        return;
      } else if (connectingPlatform === 'meta_ads') {
        // Meta Ads uses manual System User Token + Ad Account ID
        const { data, error } = await supabase.functions.invoke('meta-connect', {
          body: {
            clientId: connectingClient.id,
            token: formData.metaToken,
            accountId: formData.metaAccountId
          }
        });

        if (error) throw error;

        toast.success('Connected!', {
          description: `Connected ${connectingClient.company_name} to Meta Ads`,
        });
      }

      setConnectModalOpen(false);
      fetchClientsWithIntegrations();
    } catch (error: unknown) {
      console.error('Connection error:', error);
      toast.error('Connection Failed', {
        description: error instanceof Error ? error.message : 'Failed to connect integration',
      });
    } finally {
      setConnectLoading(false);
    }
  };

  const handleSync = async (clientId: string, platform: Platform) => {
    setSyncingClient({ clientId, platform });
    
    try {
      const syncFunctionName = platform === 'ghl' ? 'ghl-sync' :
                               platform === 'meta_ads' ? 'meta-sync' :
                               platform === 'google_ads' ? 'google-ads-sync' :
                               'sync-integration';
      const { error } = await supabase.functions.invoke(syncFunctionName, {
        body: { clientId }
      });

      if (error) throw error;

      toast.success('Sync Complete', {
        description: `Successfully synced ${platformConfig[platform].name} data`,
      });
      
      fetchClientsWithIntegrations();
    } catch (error: unknown) {
      console.error('Sync error:', error);
      toast.error('Sync Failed', {
        description: error instanceof Error ? error.message : 'Failed to sync integration',
      });
    } finally {
      setSyncingClient(null);
    }
  };

  const handleDisconnect = (clientId: string, platform: Platform) => {
    setDisconnectDialog({ open: true, clientId, platform });
  };

  const confirmDisconnect = async () => {
    const { clientId, platform } = disconnectDialog;
    if (!clientId || !platform) return;

    try {
      if (platform === 'ghl') {
        // GHL: use ghl-verify edge function for proper Vault cleanup
        const { error } = await supabase.functions.invoke('ghl-verify', {
          body: { clientId, action: 'disconnect' },
        });
        if (error) throw error;
      } else if (platform === 'meta_ads') {
        // Meta: use meta-connect edge function for proper Vault cleanup
        const { data, error } = await supabase.functions.invoke('meta-connect', {
          body: { clientId, action: 'disconnect' },
        });
        if (error) throw error;
      } else if (platform === 'google_ads') {
        const { error } = await supabase.functions.invoke('google-ads-connect', {
          body: { clientId, action: 'unlink' },
        });
        if (error) throw error;
      }

      // Data cleanup is now handled server-side inside each edge function
      // (ghl-verify, meta-connect, google-ads-connect)

      toast.success('Disconnected', {
        description: `${platformConfig[platform].name} has been disconnected and synced data cleared`,
      });

      fetchClientsWithIntegrations();
    } catch (error: unknown) {
      console.error('Disconnect error:', error);
      toast.error('Error', {
        description: 'Failed to disconnect integration',
      });
    } finally {
      setDisconnectDialog({ open: false, clientId: null, platform: null });
    }
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const diff = Date.now() - new Date(dateString).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const getLastSyncTime = (client: ClientWithIntegrations) => {
    const syncs = client.integrations
      .filter(i => i.last_sync_at)
      .map(i => new Date(i.last_sync_at!).getTime());
    
    if (syncs.length === 0) return null;
    return new Date(Math.max(...syncs)).toISOString();
  };

  const filteredClients = clients.filter(c => 
    c.company_name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase()) ||
    (c.contact_name?.toLowerCase() || '').includes(search.toLowerCase())
  );

  const toggleNotNeeded = async (clientId: string, platform: Platform) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    const current = client.integrations_not_needed || [];
    const updated = current.includes(platform)
      ? current.filter(p => p !== platform)
      : [...current, platform];
    const { error } = await supabase
      .from('clients')
      .update({ integrations_not_needed: updated })
      .eq('id', clientId);
    if (error) {
      toast.error('Failed to update');
    } else {
      setClients(prev => prev.map(c => c.id === clientId ? { ...c, integrations_not_needed: updated } : c));
    }
  };

  const IntegrationBadge = ({ client, platform }: { client: ClientWithIntegrations; platform: Platform }) => {
    const integration = getIntegration(client, platform);
    const config = platformConfig[platform];
    const isSyncing = syncingClient?.clientId === client.id && syncingClient?.platform === platform;
    const isLinking = linkingClient === client.id;
    const isNotNeeded = (client.integrations_not_needed || []).includes(platform);

    // Show "Not Needed" state for ad platforms
    if (isNotNeeded && !integration && (platform === 'google_ads' || platform === 'meta_ads')) {
      return (
        <div className="flex items-center gap-1">
          <BadgeVenture variant="default" className="gap-1 text-muted-foreground">
            Not Needed
          </BadgeVenture>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => toggleNotNeeded(client.id, platform)}
            className="h-6 text-xs px-1.5 text-muted-foreground"
            title="Mark as needed"
          >
            Undo
          </Button>
        </div>
      );
    }

    // Google Ads uses agency flow
    if (platform === 'google_ads') {
      if (agencyConnected === null) {
        return <span className="text-xs text-muted-foreground">Loading...</span>;
      }
      if (!agencyConnected) {
        return <span className="text-xs text-muted-foreground">No agency</span>;
      }
      if (!integration) {
        const nonManagerAccounts = agencyAccounts.filter(a => !a.isManager);
        return (
          <div className="flex items-center gap-1">
            <Select
              value={selectedAccountId[client.id] || ''}
              onValueChange={(val) => setSelectedAccountId(prev => ({ ...prev, [client.id]: val }))}
            >
              <SelectTrigger className="h-7 text-xs w-36">
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                {nonManagerAccounts.map(account => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name || account.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleLinkAccount(client.id)}
              disabled={isLinking || !selectedAccountId[client.id]}
              className="h-7 text-xs px-2"
            >
              <Link2 className="w-3 h-3 mr-1" />
              {isLinking ? 'Linking...' : 'Link'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toggleNotNeeded(client.id, platform)}
              className="h-7 text-xs text-muted-foreground"
            >
              Not Needed
            </Button>
          </div>
        );
      }
      // Integration exists — show account name if available, fallback to ID
      const customerId = (integration.metadata as Record<string, string>)?.customerId || integration.id;
      const accountName = agencyAccounts.find(a => a.id === customerId)?.name || (integration.metadata as Record<string, string>)?.accountName || customerId;
      return (
        <div className="flex items-center gap-1">
          <BadgeVenture variant="success" className="gap-1">
            <CheckCircle className="w-3 h-3" />
            {accountName}
          </BadgeVenture>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleSync(client.id, platform)}
            disabled={isSyncing}
            className="h-6 w-6 p-0"
          >
            <RefreshCw className={`w-3 h-3 ${isSyncing ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDisconnect(client.id, platform)}
            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
          >
            <Unlink className="w-3 h-3" />
          </Button>
        </div>
      );
    }

    if (!integration) {
      return (
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleOpenConnectModal(client, platform)}
            className="text-muted-foreground"
          >
            <Link2 className="w-3 h-3 mr-1" />
            Connect
          </Button>
          {(platform === 'google_ads' || platform === 'meta_ads') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toggleNotNeeded(client.id, platform)}
              className="h-7 text-xs text-muted-foreground"
            >
              Not Needed
            </Button>
          )}
        </div>
      );
    }

    const hasMappings = platform === 'ghl' && stageMappingsByClient.has(client.id) && stageMappingsByClient.get(client.id)!.length > 0;

    return (
      <div className="flex items-center gap-1">
        <BadgeVenture variant="success" className="gap-1">
          <CheckCircle className="w-3 h-3" />
          {config.shortName}
        </BadgeVenture>
        {platform === 'ghl' && (
          <>
            <BadgeVenture variant={hasMappings ? "success" : "default"} className="text-[10px] px-1.5 py-0">
              {hasMappings ? "Mapped" : "Not Mapped"}
            </BadgeVenture>
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setStageMappingClient({ id: client.id, name: client.company_name })}
                    className="h-6 text-xs px-2"
                  >
                    Map Stages
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Map GHL pipeline stages to Venture Hills categories (Lead, Qualified, Demo Booked, Demo Completed, Closed Won/Lost). Required for pipeline metrics and attribution.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleSync(client.id, platform)}
          disabled={isSyncing}
          className="h-6 w-6 p-0"
        >
          <RefreshCw className={`w-3 h-3 ${isSyncing ? 'animate-spin' : ''}`} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleDisconnect(client.id, platform)}
          className="h-6 w-6 p-0 text-destructive hover:text-destructive"
        >
          <Unlink className="w-3 h-3" />
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <VentureCard>
        <VentureCardContent className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </VentureCardContent>
      </VentureCard>
    );
  }

  return (
    <>
      <VentureCard>
        <VentureCardHeader className="flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <VentureCardTitle>Client Integrations</VentureCardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Manage platform connections for each client
            </p>
          </div>
          <div className="w-full sm:w-64">
            <InputVenture
              icon={<Search className="w-4 h-4" />}
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </VentureCardHeader>
        <VentureCardContent>
          <Alert className="mb-4">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>
              Only admins can connect client integrations. Clients cannot see or manage these connections.
            </AlertDescription>
          </Alert>

          {/* Agency Google Ads Connection Banner */}
          <div className="mb-4 p-3 rounded-lg border border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                <span className="font-bold text-xs text-green-500">G</span>
              </div>
              <div>
                <p className="text-sm font-medium">
                  Google Ads Agency Connection
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-muted-foreground inline ml-1 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Connect your agency Google Ads account once — then link individual client ad accounts from the dropdown</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </p>
                <p className="text-xs text-muted-foreground">
                  {agencyConnected === null ? 'Checking...' :
                   agencyConnected ? `Connected — ${agencyAccounts.filter(a => !a.isManager).length} accounts available` :
                   'Connect your agency Google Ads account to link client ad accounts'}
                </p>
              </div>
            </div>
            {agencyConnected === null ? null :
             agencyConnected ? (
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => {
                if (confirm('Disconnect agency Google Ads? This will revoke all client connections and delete all synced campaign data.')) {
                  handleAgencyDisconnect();
                }
              }}>
                Disconnect Agency
              </Button>
            ) : (
              <Button variant="gradient" size="sm" onClick={handleAgencyConnect} disabled={connectLoading}>
                {connectLoading ? 'Connecting...' : 'Connect Agency'}
              </Button>
            )}
          </div>

          {filteredClients.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <XCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No clients found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Client</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Package</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      <span className="flex items-center gap-1">
                        GoHighLevel
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3.5 h-3.5 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>GoHighLevel CRM connection. Required for pipeline tracking, demo syncing, and lead attribution. Works with both VH-owned agency accounts and client sub-accounts.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </span>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      <span className="flex items-center gap-1">
                        Google Ads
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3.5 h-3.5 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>Google Ads connection via agency account. Links client ad accounts for campaign spend, clicks, and conversion tracking. Mark as 'Not Needed' if client doesn't run Google ads.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </span>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      <span className="flex items-center gap-1">
                        Meta Ads
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3.5 h-3.5 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p>Meta (Facebook/Instagram) Ads connection. Requires a System User Token and Ad Account ID. Syncs campaign performance data daily. Mark as 'Not Needed' if client doesn't run Meta ads.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </span>
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last Sync</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => (
                    <tr 
                      key={client.id} 
                      className="border-b border-border hover:bg-surface transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium">{client.company_name}</p>
                          <p className="text-sm text-muted-foreground">{client.contact_name || client.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <BadgeVenture variant={client.package === 'elite' ? 'warning' : 'info'}>
                          {client.package}
                        </BadgeVenture>
                      </td>
                      <td className="py-3 px-4">
                        <IntegrationBadge client={client} platform="ghl" />
                      </td>
                      <td className="py-3 px-4">
                        <IntegrationBadge client={client} platform="google_ads" />
                      </td>
                      <td className="py-3 px-4">
                        <IntegrationBadge client={client} platform="meta_ads" />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {formatRelativeTime(getLastSyncTime(client))}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedClientDetails(client);
                            setDetailsModalOpen(true);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>

      {/* Connect Platform Modal */}
      <Dialog open={connectModalOpen} onOpenChange={setConnectModalOpen}>
        <DialogContent className="bg-surface border-border">
          <DialogHeader>
            <DialogTitle>
              Connect {connectingPlatform && platformConfig[connectingPlatform].name}
            </DialogTitle>
            <DialogDescription>
              For client: {connectingClient?.company_name}
            </DialogDescription>
          </DialogHeader>
          
          {connectingPlatform === 'ghl' ? (
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  A popup window will open where the client can select their GoHighLevel location to authorize.
                  Make sure popups are enabled.
                </AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground">
                Click the button below to start the GoHighLevel OAuth authorization flow.
                The client will choose which location to connect.
              </p>
            </div>
          ) : connectingPlatform === 'meta_ads' ? (
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  You'll need a Meta System User Token and Ad Account ID.
                  The token can be generated in Meta Business Settings under System Users.
                  The Ad Account ID starts with "act_".
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <Label>System User Token *</Label>
                <Input
                  value={formData.metaToken}
                  onChange={(e) => setFormData({ ...formData, metaToken: e.target.value })}
                  placeholder="Enter Meta System User token"
                />
              </div>

              <div className="space-y-2">
                <Label>Ad Account ID *</Label>
                <Input
                  value={formData.metaAccountId}
                  onChange={(e) => setFormData({ ...formData, metaAccountId: e.target.value })}
                  placeholder="act_123456789"
                />
              </div>
            </div>
          ) : connectingPlatform === 'google_ads' ? (
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  Google Ads uses the agency connection. Close this dialog and use the "Connect Agency" button at the top of the integrations table.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="w-4 h-4" />
                <AlertDescription>
                  A popup window will open to authorize access to the client's Google Ads account.
                  Make sure popups are enabled.
                </AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground">
                Click the button below to start the Google Ads OAuth authorization flow.
              </p>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConnectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConnect}
              disabled={connectLoading || (connectingPlatform === 'meta_ads' && (!formData.metaToken || !formData.metaAccountId))}
            >
              {connectLoading ? 'Connecting...' : connectingPlatform === 'ghl' ? 'Authorize GoHighLevel' : connectingPlatform === 'google_ads' ? 'Authorize Google Ads' : `Connect ${connectingPlatform && platformConfig[connectingPlatform].name}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Integration Details Modal */}
      <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
        <DialogContent className="bg-surface border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle>Integration Details - {selectedClientDetails?.company_name}</DialogTitle>
            <DialogDescription>View connection status and sync history</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedClientDetails && (Object.keys(platformConfig) as Platform[]).map(platform => {
              const integration = getIntegration(selectedClientDetails, platform);
              const config = platformConfig[platform];
              
              return (
                <div key={platform} className="p-4 rounded-lg border border-border">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg ${config.color}/20 flex items-center justify-center`}>
                        <span className={`font-bold text-xs ${config.color.replace('bg-', 'text-')}`}>
                          {config.shortName}
                        </span>
                      </div>
                      <span className="font-medium">{config.name}</span>
                    </div>
                    {integration ? (
                      <BadgeVenture variant="success">Connected</BadgeVenture>
                    ) : (
                      <BadgeVenture variant="default">Not Connected</BadgeVenture>
                    )}
                  </div>
                  
                  {integration && (
                    <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                      <div>
                        <span className="text-muted-foreground">Last Sync:</span>
                        <span className="ml-2">{formatRelativeTime(integration.last_sync_at)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Total Syncs:</span>
                        <span className="ml-2">{integration.sync_count || 0}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Status:</span>
                        <span className="ml-2">{integration.status}</span>
                      </div>
                      {integration.metadata && (
                        <div>
                          <span className="text-muted-foreground">Account:</span>
                          <span className="ml-2">
                            {(integration.metadata as Record<string, string>).accountName || 
                             (integration.metadata as Record<string, string>).locationName || 
                             'Connected'}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDetailsModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={disconnectDialog.open} onOpenChange={(open) => {
        if (!open) setDisconnectDialog({ open: false, clientId: null, platform: null });
      }}>
        <DialogContent className="bg-surface border-border">
          <DialogHeader>
            <DialogTitle>Disconnect Integration</DialogTitle>
            <DialogDescription>
              Are you sure you want to disconnect {disconnectDialog.platform && platformConfig[disconnectDialog.platform]?.name}? This will stop data syncing.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDisconnectDialog({ open: false, clientId: null, platform: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDisconnect}>
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stage Mapping Modal */}
      <StageMappingModal
        open={!!stageMappingClient}
        onOpenChange={(open) => { if (!open) setStageMappingClient(null); }}
        clientId={stageMappingClient?.id ?? ""}
        clientName={stageMappingClient?.name ?? ""}
      />
    </>
  );
}
